import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
  Button,
  Divider,
} from '@mui/material';
import {
  Search,
  Refresh,
  RequestQuote,
  AttachMoney,
  CalendarToday,
  Business,
  Visibility,
  CheckCircle,
  Warning,
  Schedule,
  Cancel,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Quotation, Account } from '../services/crmService';

const QuotationsPage: React.FC = () => {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Detail dialog state
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewingQuotation, setViewingQuotation] = useState<Quotation | null>(null);

  // Fetch quotations
  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await crmService.getQuotations();
      const quotationsData = Array.isArray(response) ? response :
                            response.quotations ? response.quotations :
                            response.data ? response.data : [];
      setQuotations(quotationsData);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setSnackbar({ open: true, message: 'Failed to fetch quotations', severity: 'error' });
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  // Get account name
  const getAccountName = (account: string | Account | undefined): string => {
    if (!account) return '-';
    if (typeof account === 'string') return account;
    return account.name || '-';
  };

  // Get partner name
  const getPartnerName = (partner: string | Account | undefined): string => {
    if (!partner) return '-';
    if (typeof partner === 'string') return partner;
    return partner.name || '-';
  };

  // Filter quotations based on search
  const filteredQuotations = quotations.filter(q =>
    `${q.quotationNumber} ${q.accountName || getAccountName(q.account)} ${q.opportunityName || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      'Draft': 'default',
      'Sent': 'info',
      'Viewed': 'info',
      'Accepted': 'success',
      'Rejected': 'error',
      'Expired': 'error',
      'Converted': 'success',
      'Cancelled': 'default',
    };
    return colors[status] || 'default';
  };

  // Format currency
  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate days until expiry
  const getDaysUntilExpiry = (validUntil: Date | string | undefined) => {
    if (!validUntil) return null;
    const now = new Date();
    const end = new Date(validUntil);
    const diff = end.getTime() - now.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  // Handle view quotation details
  const handleViewQuotation = async (quotation: Quotation) => {
    try {
      if (quotation._id) {
        const detailed = await crmService.getQuotation(quotation._id);
        setViewingQuotation(detailed);
      } else {
        setViewingQuotation(quotation);
      }
    } catch {
      setViewingQuotation(quotation);
    }
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setViewingQuotation(null);
  };

  // Stats calculations
  const totalQuotations = quotations.length;
  const acceptedQuotations = quotations.filter(q => q.status === 'Accepted').length;
  const expiringSoon = quotations.filter(q => {
    const days = getDaysUntilExpiry(q.validUntil);
    return days !== null && days > 0 && days <= 7 && ['Draft', 'Sent', 'Viewed'].includes(q.status);
  }).length;
  const totalValue = quotations.reduce((sum, q) => sum + (q.totalAmount || 0), 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RequestQuote sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{totalQuotations}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Quotations</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{acceptedQuotations}</Typography>
                  <Typography variant="body2" color="text.secondary">Accepted</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Warning sx={{ color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{expiringSoon}</Typography>
                  <Typography variant="body2" color="text.secondary">Expiring Soon</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachMoney sx={{ color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{formatCurrency(totalValue)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Value</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Quotations
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchQuotations}
          >
            Refresh
          </Button>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search quotations by number, account, or opportunity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Quotations Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Quotation #</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Opportunity</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={8}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredQuotations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No quotations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQuotations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((quotation) => {
                      const daysUntilExpiry = getDaysUntilExpiry(quotation.validUntil);
                      return (
                        <TableRow key={quotation._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {quotation.quotationNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business fontSize="small" color="action" />
                              {quotation.accountName || getAccountName(quotation.account)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {getPartnerName(quotation.partner)}
                          </TableCell>
                          <TableCell>
                            {quotation.opportunityName || (typeof quotation.opportunity === 'object' && quotation.opportunity ? (quotation.opportunity as any).name : '-')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(quotation.totalAmount, quotation.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday fontSize="small" color="action" />
                                {formatDate(quotation.validUntil)}
                              </Box>
                              {daysUntilExpiry !== null && ['Draft', 'Sent', 'Viewed'].includes(quotation.status) && (
                                <Typography
                                  variant="caption"
                                  color={daysUntilExpiry <= 0 ? 'error' : daysUntilExpiry <= 7 ? 'warning.main' : 'text.secondary'}
                                >
                                  {daysUntilExpiry > 0 ? `${daysUntilExpiry} days left` : 'Expired'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={quotation.status}
                              size="small"
                              color={getStatusColor(quotation.status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewQuotation(quotation)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredQuotations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Quotation Detail Dialog */}
        <Dialog open={openDetailDialog} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Quotation Details
          </DialogTitle>
          <DialogContent>
            {viewingQuotation && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {viewingQuotation.quotationNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Revision {viewingQuotation.revisionNumber || 1}
                      </Typography>
                    </Box>
                    <Chip
                      label={viewingQuotation.status}
                      color={getStatusColor(viewingQuotation.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Account</Typography>
                  <Typography>{viewingQuotation.accountName || getAccountName(viewingQuotation.account)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Partner</Typography>
                  <Typography>{getPartnerName(viewingQuotation.partner)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Opportunity</Typography>
                  <Typography>
                    {viewingQuotation.opportunityName || (typeof viewingQuotation.opportunity === 'object' && viewingQuotation.opportunity ? (viewingQuotation.opportunity as any).name : '-')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contact</Typography>
                  <Typography>
                    {typeof viewingQuotation.contact === 'object' && viewingQuotation.contact
                      ? `${(viewingQuotation.contact as any).firstName || ''} ${(viewingQuotation.contact as any).lastName || ''}`.trim()
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Issue Date</Typography>
                  <Typography>{formatDate(viewingQuotation.issueDate)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Valid Until</Typography>
                  <Typography>{formatDate(viewingQuotation.validUntil)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Currency</Typography>
                  <Typography>{viewingQuotation.currency || 'USD'}</Typography>
                </Grid>

                {/* Line Items */}
                {viewingQuotation.lineItems && viewingQuotation.lineItems.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Line Items
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewingQuotation.lineItems.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">{formatCurrency(item.unitPrice, viewingQuotation.currency)}</TableCell>
                              <TableCell align="right">
                                {item.discount ? `${item.discount}${item.discountType === 'percentage' ? '%' : ''}` : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {item.tax ? formatCurrency(item.tax, viewingQuotation.currency) : '-'}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(item.total, viewingQuotation.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Totals */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={3}>
                  <Typography variant="body2" align="right">Subtotal:</Typography>
                  <Typography variant="body2" align="right">Discount:</Typography>
                  <Typography variant="body2" align="right">Tax:</Typography>
                  <Typography variant="subtitle1" align="right" fontWeight="bold" sx={{ mt: 0.5 }}>Total:</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" align="right">{formatCurrency(viewingQuotation.subtotal, viewingQuotation.currency)}</Typography>
                  <Typography variant="body2" align="right" color="error">
                    {viewingQuotation.totalDiscount ? `-${formatCurrency(viewingQuotation.totalDiscount, viewingQuotation.currency)}` : '-'}
                  </Typography>
                  <Typography variant="body2" align="right">
                    {viewingQuotation.totalTax ? formatCurrency(viewingQuotation.totalTax, viewingQuotation.currency) : '-'}
                  </Typography>
                  <Typography variant="subtitle1" align="right" fontWeight="bold" color="primary" sx={{ mt: 0.5 }}>
                    {formatCurrency(viewingQuotation.totalAmount, viewingQuotation.currency)}
                  </Typography>
                </Grid>

                {/* Payment Terms & Notes */}
                {viewingQuotation.paymentTerms && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Payment Terms</Typography>
                    <Typography>{viewingQuotation.paymentTerms}</Typography>
                  </Grid>
                )}
                {viewingQuotation.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography>{viewingQuotation.notes}</Typography>
                  </Grid>
                )}
                {viewingQuotation.termsAndConditions && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Terms & Conditions</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {viewingQuotation.termsAndConditions}
                    </Typography>
                  </Grid>
                )}

                {/* Converted Invoice */}
                {viewingQuotation.convertedToInvoice && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Chip
                      label={`Converted to Invoice: ${typeof viewingQuotation.convertedToInvoice === 'object' ? (viewingQuotation.convertedToInvoice as any).invoiceNumber : viewingQuotation.convertedToInvoice}`}
                      color="success"
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default QuotationsPage;
